/* Loupe – quick view for product cards.
   Loaded by snippets/product-card.liquid when a section passes quick_view: true (once per card, so it guards itself).
   Content comes from sections/quick-view.liquid via the Section Rendering API: <product url>?section_id=quick-view.
   Variant changes inside the modal reuse <loupe-variant-picker>, add to cart reuses <loupe-product-form data-ajax>. */
(() => {
  if (window.customElements.get('loupe-quick-view')) return;

  const SECTION = 'quick-view';
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const CLOSE_SVG = '<svg class="icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6 6 18"/></svg>';
  const SKELETON = '<div class="qv" aria-hidden="true"><div class="qv__media"><span class="qv-sk qv-sk--media"></span></div>'
    + '<div class="qv__info"><span class="qv-sk" style="width:28%"></span><span class="qv-sk qv-sk--title"></span><span class="qv-sk" style="width:22%"></span>'
    + '<span class="qv-sk" style="width:100%;margin-top:30px"></span><span class="qv-sk" style="width:86%"></span><span class="qv-sk qv-sk--btn"></span></div></div>';

  /* fetch once per URL (prefetch on hover/focus fills the same cache) */
  const cache = new Map();
  const sectionUrl = (href) => { const u = new URL(href, location.origin); u.searchParams.set('section_id', SECTION); return u.pathname + u.search; };
  const load = (href) => {
    const url = sectionUrl(href);
    if (!cache.has(url)) {
      const p = fetch(url, { credentials: 'same-origin' }).then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.text(); });
      p.catch(() => cache.delete(url));
      cache.set(url, p);
    }
    return cache.get(url);
  };
  /* the card's title link follows the swatch the shopper picked, so open that variant */
  const hrefOf = (btn) => { const a = btn.closest('.card') && btn.closest('.card').querySelector('.card__title a[href]'); return (a && a.getAttribute('href')) || btn.dataset.quickView; };

  class LoupeQuickView extends HTMLElement {
    connectedCallback() {
      if (this.panel) return;
      this.hidden = true;
      this.innerHTML = '<div class="qv-overlay" data-qv-close></div>'
        + '<div class="qv-panel" role="dialog" aria-modal="true" tabindex="-1">'
        + `<button type="button" class="icon-btn qv-close" data-qv-close aria-label="Close">${CLOSE_SVG}</button>`
        + '<div class="qv-body" data-qv-body></div></div>';
      this.panel = this.querySelector('.qv-panel');
      this.body = this.querySelector('[data-qv-body]');
      this.closeBtn = this.querySelector('.qv-close');

      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-qv-close]')) { e.preventDefault(); this.close(); return; }
        const step = e.target.closest('[data-qv-step]');
        if (step) {
          const track = this.querySelector('.qv .gallery');
          if (track) track.scrollBy({ left: +step.dataset.qvStep * track.clientWidth, behavior: reduce.matches ? 'auto' : 'smooth' });
        }
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) { e.preventDefault(); e.stopPropagation(); this.close(); }
        else if (e.key === 'Tab') this.trap(e);
      });
      /* add to cart from inside the modal: close it once the cart has updated, the cart drawer takes over */
      this.addEventListener('submit', (e) => { if (e.target.closest('loupe-product-form')) this.adding = true; }, true);
      document.addEventListener('loupe:cart-updated', () => { if (this.isOpen && this.adding) this.close(); });
    }

    /* instant first paint: the card's own photo, title and price while the full content loads */
    preview(opener) {
      const card = opener && opener.closest && opener.closest('.card'); if (!card) return '';
      const img = card.querySelector('.card__media img'); const src = img && (img.currentSrc || img.src);
      const esc = (t) => String(t || '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
      const title = (card.querySelector('.card__title') || {}).textContent || '';
      const price = (card.querySelector('.price') || {}).innerHTML || '';
      return '<div class="qv qv--preview" aria-hidden="true"><div class="qv__media"><div class="qv-pre-img">' + (src ? `<img src="${esc(src)}" alt="">` : '<span class="qv-sk qv-sk--media"></span>') + '</div></div>'
        + `<div class="qv__info"><h2 class="qv-pre-title">${esc(title.trim())}</h2><div class="price">${price}</div>`
        + '<span class="qv-sk" style="width:100%;margin-top:30px"></span><span class="qv-sk" style="width:86%"></span><span class="qv-sk qv-sk--btn"></span></div></div>';
    }

    async open(href, opener) {
      const s = (opener && opener.dataset) || {};
      this.opener = opener || document.activeElement;
      this.href = href;
      const token = this.token = (this.token || 0) + 1;
      clearTimeout(this.hideT);
      this.closeBtn.setAttribute('aria-label', s.qvClose || 'Close');
      this.panel.removeAttribute('aria-labelledby');
      this.panel.setAttribute('aria-label', s.qvLoading || 'Loading');
      this.body.setAttribute('aria-busy', 'true');
      this.body.innerHTML = this.preview(opener) || SKELETON;
      this.body.scrollTop = 0;
      this.adding = false;

      this.hidden = false;
      this.isOpen = true;
      document.body.classList.add('is-locked');
      void this.offsetWidth;   // commit the hidden state so the sheet/panel animates in
      this.classList.add('is-open');
      requestAnimationFrame(() => this.closeBtn.focus());

      try {
        const html = await load(href);
        if (token !== this.token) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const content = doc.querySelector(`[data-section-id="${SECTION}"]`);
        if (!content) throw new Error('empty');
        this.body.replaceChildren(document.adoptNode(content));   // custom elements (gallery, picker, form) upgrade on insert
        if (this.body.querySelector('#QuickViewTitle')) {
          this.panel.removeAttribute('aria-label');
          this.panel.setAttribute('aria-labelledby', 'QuickViewTitle');
        }
      } catch (err) {
        if (token !== this.token) return;
        const box = document.createElement('div'); box.className = 'qv-error';
        const p = document.createElement('p'); p.textContent = s.qvError || '';
        const a = document.createElement('a'); a.className = 'link'; a.href = href; a.textContent = s.qvDetails || href;
        box.append(p, a); this.body.replaceChildren(box);
        this.panel.removeAttribute('aria-label'); this.panel.setAttribute('aria-label', s.qvError || '');
      } finally {
        if (token === this.token) this.body.removeAttribute('aria-busy');
      }
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.adding = false;
      this.token = (this.token || 0) + 1;   // drop a response that is still on its way
      this.classList.remove('is-open');
      if (!document.querySelector('.drawer.is-open')) document.body.classList.remove('is-locked');
      const done = () => { if (!this.isOpen) { this.hidden = true; this.body.replaceChildren(); } };
      if (reduce.matches) done(); else this.hideT = setTimeout(done, 400);
      const o = this.opener;
      if (o && o.isConnected && o.focus) o.focus({ preventScroll: true });
    }

    trap(e) {
      const f = [...this.panel.querySelectorAll(FOCUSABLE)].filter((x) => x.getClientRects().length && !x.closest('[hidden], noscript'));
      if (!f.length) { e.preventDefault(); return; }
      const first = f[0], last = f[f.length - 1], cur = document.activeElement;
      if (!this.panel.contains(cur) || cur === this.panel) { e.preventDefault(); (e.shiftKey ? last : first).focus(); }
      else if (e.shiftKey && cur === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && cur === last) { e.preventDefault(); first.focus(); }
    }
  }
  customElements.define('loupe-quick-view', LoupeQuickView);

  const modal = () => document.querySelector('loupe-quick-view') || document.body.appendChild(document.createElement('loupe-quick-view'));

  document.addEventListener('click', (e) => {
    const b = e.target.closest && e.target.closest('button[data-quick-view]'); if (!b) return;
    e.preventDefault();
    modal().open(hrefOf(b), b);
  });

  /* prefetch: hovering a card for a moment, or focusing / touching the button */
  const saveData = navigator.connection && navigator.connection.saveData;
  const prefetch = (b) => { if (b) load(hrefOf(b)).catch(() => {}); };
  let hoverCard = null, hoverT;
  document.addEventListener('mouseover', (e) => {
    const card = e.target.closest ? e.target.closest('.card') : null;
    if (card === hoverCard) return;
    hoverCard = card; clearTimeout(hoverT);
    const b = card && card.querySelector('button[data-quick-view]'); if (!b) return;
    if (b.contains(e.target)) prefetch(b);
    else if (!saveData) hoverT = setTimeout(() => prefetch(b), 250);
  }, { passive: true });
  document.addEventListener('focusin', (e) => prefetch(e.target.closest && e.target.closest('button[data-quick-view]')));
  document.addEventListener('touchstart', (e) => prefetch(e.target.closest && e.target.closest('button[data-quick-view]')), { passive: true });
})();
